import { useState, useEffect } from 'react';
import {
  DatePicker,
  Dropdown,
  Modal,
  TextArea,
} from '@triumph/design-system';
import { DisputedReasons } from '@/app/features/InvoiceBillingReminder/billingReminder.types';
import { getDisputedReasons } from '@/app/shared/api/endpoints/disputedReasons';
import { option } from '@triumph/design-system/components/Dropdown/Dropdown.types';
import { ModalDatePickerWrapper } from './UpdatePaymentStatusModal.StyledComponents';
import { sortWithValueLast } from '@/app/shared/utils/utils';

type PaymentStatus = 'willPay' | 'willNotPay';

const STATUS_OPTIONS: option[] = [
  { id: 'willPay', value: 'willPay', displayValue: 'Will Pay' },
  { id: 'willNotPay', value: 'willNotPay', displayValue: 'Will Not Pay' },
];

interface UpdatePaymentStatusModalProps {
  isVisible: boolean;
  handleClose: () => void;
  selectedInvoices: Set<string>;
  onBulkPaymentDateChange: (
    invoiceNumbers: string[],
    selectedPaymentDate: string,
    note: string
  ) => Promise<void>;
  onBulkWillNotPayChange: (
    invoiceNumbers: string[],
    disputedReasonId: string,
    disputedReasonDesc: string,
    note: string
  ) => Promise<void>;
  isSubmitting?: boolean;
}


export const UpdatePaymentStatusModal: React.FC<UpdatePaymentStatusModalProps> = ({
  isVisible,
  handleClose,
  selectedInvoices,
  onBulkPaymentDateChange,
  onBulkWillNotPayChange,
  isSubmitting = false,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<PaymentStatus>('willPay');
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [willPayNote, setWillPayNote] = useState<string>('');

  const [disputedReasons, setDisputedReasons] = useState<option[]>([]);
  const [disputedReasonsRaw, setDisputedReasonsRaw] = useState<DisputedReasons[]>([]);
  const [selectedReasonId, setSelectedReasonId] = useState<string>('');
  const [noteText, setNoteText] = useState<string>('');

  const selectedInvoicesList = Array.from(selectedInvoices);
  const selectedCount = selectedInvoicesList.length;

  const selectedReasonRaw = disputedReasonsRaw.find((r) => r.id.toString() === selectedReasonId);
  const isNoteRequired = selectedReasonRaw?.noteRequired ?? false;
  const selectedReasonOption = disputedReasons.find((r) => r.value === selectedReasonId);

  useEffect(() => {
    const fetchDisputedReasons = async () => {
      const options: DisputedReasons[] = await getDisputedReasons();
      const mappedOptions = options.map((item) => ({
        id: item.id.toString(),
        value: item.id.toString(),
        displayValue: item.statusReasonCodeName.toString(),
      }));
      const sortOtherLast = sortWithValueLast(mappedOptions, 'displayValue', 'other');
      setDisputedReasons(sortOtherLast);
      setDisputedReasonsRaw(options);
    };
    fetchDisputedReasons();
  }, []);

  const handleDateChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      setSelectedDate(date[0] || null);
    } else {
      setSelectedDate(date);
    }
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = event.target.value as PaymentStatus;
    setSelectedStatus(newStatus);
    setSelectedReasonId('');
    setNoteText('');
    setWillPayNote('');

    setSelectedDate(null);
  };

  const handleReasonChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedReasonId(event.target.value);
    setNoteText('');
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(event.target.value);
  };

  const formatDateToString = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  const handleSubmit = async () => {
    if (selectedCount === 0) return;

    try {
      if (selectedStatus === 'willPay') {
        if (!selectedDate) return;
        await onBulkPaymentDateChange(selectedInvoicesList, formatDateToString(selectedDate), willPayNote);
      } else {
        await onBulkWillNotPayChange(selectedInvoicesList, selectedReasonId, selectedReasonOption?.displayValue ?? '', noteText);
      }

      resetForm();
      handleClose();
    } catch (error) {
      console.error('Failed to submit updates:', error);
    }
  };

  const handleModalClose = () => {
    if (isSubmitting) return;
    resetForm();
    handleClose();
  };

  const resetForm = () => {
    setSelectedStatus('willPay');
    setSelectedDate(null);
    setSelectedReasonId('');
    setNoteText('');
    setWillPayNote('');
  };

  const isSubmitDisabled = (): boolean => {
    if (selectedCount === 0) return true;
    if (isSubmitting) return true;

    if (selectedStatus === 'willPay') {
      return !selectedDate;
    } else {
      if (!selectedReasonId) return true;
      if (isNoteRequired && !noteText) return true;
      return false;
    }
  };

  const renderWillPayContent = () => (
    <div>
      <ModalDatePickerWrapper>
        <DatePicker
          id="bulk-will-pay-date-picker"
          onChange={handleDateChange}
          allowRange={false}
          startDate={selectedDate || undefined}
          defaultValue={selectedDate || undefined}
          startDateLabel="Select Date"
          inputWidth="100%"
        />
      </ModalDatePickerWrapper>
      <TextArea
        id="bulk-will-pay-note"
        labelText="Note (optional)"
        onChange={(e) => setWillPayNote(e.target.value)}
        value={willPayNote}
        characterLimit={150}
        disabled={false}
      />
    </div>
  );

  const renderWillNotPayContent = () => (
    <div>
      <Dropdown
        id="bulk-disputed-reason"
        labelText="Reason"
        options={disputedReasons}
        selectedValue={selectedReasonId}
        onChange={handleReasonChange}
        placeholder="Select a reason"
      />
      <TextArea
        id="bulk-will-not-pay-note"
        labelText={isNoteRequired ? 'Note (required)' : 'Note (optional)'}
        onChange={handleNoteChange}
        value={noteText}
        characterLimit={150}
        disabled={false}
      />
    </div>
  );

  return (
    <Modal
      id="set-pay-date-modal"
      onClose={handleModalClose}
      visible={isVisible}
      headerContent="Update Payment Status"
      onPrimaryAction={handleSubmit}
      onSecondaryAction={handleModalClose}
      primaryButtonText={isSubmitting ? 'Updating...' : 'Update'}
      secondaryButtonText="Cancel"
      primaryAdditionalProps={{ disabled: isSubmitDisabled() }}
      secondaryAdditionalProps={{ disabled: isSubmitting }}
      bodyContent={
        <div>
          <Dropdown
            id="payment-status-dropdown"
            labelText="Status"
            options={STATUS_OPTIONS}
            selectedValue={selectedStatus}
            onChange={handleStatusChange}
            disabled={isSubmitting}
          />

          {selectedStatus === 'willPay'
            ? renderWillPayContent()
            : renderWillNotPayContent()
          }
        </div>
      }
    />
  );
};
